/**
 * @fileoverview Permanent file badge for the conversation message history.
 *
 * Hidden behind the `fileUpload` feature flag. Once a message with
 * attachments is sent, its chips become read-only badges in the conversation
 * history. A badge shows the file-type icon, the (truncated) filename, and the
 * formatted size. Unlike {@link FileChip}, it has no remove button and no
 * uploading/error states.
 *
 * NOTE: This component is built but NOT yet wired into the message history.
 * See the TODO in `UserMessage.tsx` for where it will eventually render.
 */

import React from "react";
import { Badge } from "../ui/badge";
import { UploadFile } from "./types";

import {
  formatFileSize,
  getFileTypeIcon,
  truncateFilename,
} from "./fileUploadUtils";

interface FileBadgeProps {
  /** The sent attachment to render as a read-only badge. */
  file: UploadFile;
}

/**
 * Renders a single read-only file badge for the conversation history.
 */
export const FileBadge = ({ file }: FileBadgeProps) => {
  const TypeIcon = getFileTypeIcon(file.type);

  return (
    <Badge
      variant="secondary"
      data-testid="file-badge"
      className="max-w-xs gap-1.5"
    >
      <TypeIcon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate" title={file.name}>
        {truncateFilename(file.name)}
      </span>
      <span className="flex-shrink-0 text-muted-foreground">
        {formatFileSize(file.size)}
      </span>
    </Badge>
  );
};

export default FileBadge;
